import { useMemo } from "react";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import * as Yup from "yup";
import routes from "./routes";
import {
  separateSourcingPeople,
  toConsignmentFormValues,
} from "./consignmentFormValues";
import { Portal, getChangedFields } from "../../../../util";
import AuthRoute from "../../../../components/AuthRoute";
import {
  getSalesPeopleRowsError,
  toSalesPeopleInput,
} from "../../../../components/Functional/SalesPeopleSplitEditor/salesPeopleSplitEditorUtils";
import { FETCH_CONSIGNMENT as fetchConsignment } from "../../../../graphql/query/Consignment";
import { UPDATE_CONSIGNMENT as updateConsignmentMutation } from "../../../../graphql/mutation/Consignment";
import { SET_CONSIGNMENT_SALES_PEOPLE } from "../../../../graphql/mutation/SalesPeopleSplit";
import { FormSaveStatus, NavBar, Spinner } from "../../../../components/ui";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...remainingProps} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const consignmentFragment = {
  // Consignment scalar fields - Form
  typeId: true,
  date: true,
  currencyId: true,
  due: true,
  terms: true,
  notes: true,
  noOfConsignmentWorks: true,
  consignerId: true,
  contactId: true,
  consigneeCompany: true,
  exhibitionId: true,
  updatedAt: true,
  // Consignment scalar fields - Schedule (Collection)
  colContactId: true,
  colCompany: true,
  colAddress: true,
  colAddress2: true,
  colAddress3: true,
  colTown: true,
  colCounty: true,
  colCountryId: true,
  colPostcode: true,
  // Consignment scalar fields - Schedule (Return)
  retContactId: true,
  retCompany: true,
  retAddress: true,
  retAddress2: true,
  retAddress3: true,
  retTown: true,
  retCounty: true,
  retCountryId: true,
  retPostcode: true,
  // Consignment scalar fields - Registrar
  registrarId: true,
  // Consignment scalar fields - Schedule (Consignee)
  consigneeTel: true,
  consigneeFax: true,
  consigneeEmail: true,
  // Consignment scalar fields - Schedule (Sales)
  salesUserId: true,
  artistUserId: true,
  channelId: true,
  statusId: true,
  shipmentNotes: true,
  photoMaterial: true,
  credit: true,
  guarantee: true,
  // Consignment associations
  consignmentSalesPeople: true,
  exhibition: {
    fullDate: true,
  },
  company: {},
  consigner: {
    company: true,
    typeId: true,
  },
  contact: {
    name: true,
    company: true,
  },
};

const ConsignmentForm = props => {
  const { id: consignmentId } = useParams();
  const { data: { consignment } = {}, loading } = useQuery(
    fetchConsignment(consignmentFragment),
    { skip: !consignmentId, variables: { id: +consignmentId } },
  );
  const [updateConsignment] = useMutation(
    updateConsignmentMutation(consignmentFragment),
    { refetchQueries: ["FetchConsignment"] },
  );
  const [setConsignmentSalesPeople] = useMutation(
    SET_CONSIGNMENT_SALES_PEOPLE,
    { refetchQueries: ["FetchConsignment"] },
  );
  const initialValues = useMemo(
    () => toConsignmentFormValues(consignment),
    [consignment],
  );

  const saveSourcingPeople = async sourcingPeople => {
    const rowsError = getSalesPeopleRowsError(sourcingPeople);
    if (rowsError) throw new Error(rowsError);

    const { data } = await setConsignmentSalesPeople({
      variables: {
        input: {
          consignmentId: +consignmentId,
          salesPeople: toSalesPeopleInput(sourcingPeople),
        },
      },
    });
    if (!data?.setConsignmentSalesPeople?.success) {
      throw new Error(
        data?.setConsignmentSalesPeople?.error ||
          "The sourcing people could not be saved",
      );
    }
  };

  const formik = useAutosaveFormik({
    initialValues,
    validationSchema: Yup.object({
      typeId: Yup.string().required("Please select a type"),
      date: Yup.date().required("Please enter a date"),
      due: Yup.date()
        .nullable(true)
        .min(Yup.ref("date"), "Due date can't be before date")
        .when("typeId", {
          is: val => {
            val = parseInt(val);
            return val === 2;
          },
          then: schema => schema.required("Please enter a due date"),
        }),
      currencyId: Yup.number().required("Please select a currency"),
      notes: Yup.string().nullable(true),
    }),
    onSave: async (values, savedValues) => {
      const {
        consignmentValues,
        savedConsignmentValues,
        sourcingPeople,
        sourcingPeopleChanged,
      } = separateSourcingPeople(values, savedValues);
      const changedFields = getChangedFields(
        consignmentValues,
        savedConsignmentValues,
      );

      if (
        "contactId" in changedFields &&
        typeof changedFields?.contactId !== "number"
      ) {
        changedFields.consigneeCompany = null;
      }

      if (Object.keys(changedFields).length > 0) {
        const { data } = await updateConsignment({
          variables: {
            input: {
              id: +consignmentId,
              ...changedFields,
            },
          },
        });

        if (!data?.updateConsignment?.success) {
          throw new Error(
            data?.updateConsignment?.error ||
              "The consignment could not be saved",
          );
        }
      }

      if (sourcingPeopleChanged) {
        await saveSourcingPeople(sourcingPeople);
      }
    },
  });

  const bodyProps = {
    ...props,
    consignmentId: +consignmentId,
    consignment,
    formik,
  };

  return (
    <div className="relative flex min-h-full">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="min-w-0 flex-1 overflow-x-auto py-4">
        <Body {...bodyProps} loading={loading} />
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <ConsignmentForm key={id} {...props} />;
};

export default Wizard;
